import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Output,
  Renderer2,
  ViewChild,
  forwardRef,
} from '@angular/core'
import {
  trigger,
  style,
  transition,
  animate,
  AnimationEvent,
} from '@angular/animations'
import { ZIndexUtils } from 'primeng/utils'
import { DomHandler, ConnectedOverlayScrollHandler } from 'primeng/dom'
import { PrimeNGConfig } from 'primeng/api'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

@Component({
  selector: 'basin-timeframe-picker',
  templateUrl: './timeframe-picker.component.html',
  styleUrls: ['./timeframe-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeframePickerComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('overlayAnimation', [
      transition('void => visible', [
        style({ opacity: 0, transform: 'scaleY(0.8)' }),
        animate(
          '{{showTransitionParams}}',
          style({ opacity: 1, transform: '*' })
        ),
      ]),
      transition('visible => void', [
        animate('{{hideTransitionParams}}', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class TimeframePickerComponent implements ControlValueAccessor {
  @ViewChild('contentWrapper', { static: false }) contentViewChild: ElementRef
  @ViewChild('container', { static: false }) containerViewChild: ElementRef
  overlay: HTMLDivElement
  focus: boolean
  documentClickListener: any
  overlayVisible: boolean
  documentResizeListener: any
  scrollHandler: any
  timePickerTimer: any
  onModelChange: Function = () => {}
  onModelTouched: Function = () => {}

  displayValue: string
  displayMinutes: number = 0
  displaySeconds: number = 0
  totalSeconds: number

  @Input() styleClass: string
  @Input() style: string
  @Input() inline: boolean = false
  @Input() autoZIndex: boolean = true
  @Input() baseZIndex: number = 0
  @Input() appendTo: any
  @Input() showOnFocus: boolean = true
  @Input() focusTrap: boolean = true
  @Input() disabled: boolean = false
  @Input() readonly: boolean = false
  @Input() set ngModel(seconds: number) {
    this.writeValue(seconds)
  }
  writeValue(seconds: number) {
    this.displayMinutes = seconds ? Math.trunc(seconds / 60) : 0
    this.displaySeconds = seconds ? seconds % 60 : 0
    if (this.displayMinutes != 0 && this.displaySeconds != 0) {
      this.setDisplayValue()
    }
  }
  @Output() ngModelChange: EventEmitter<number> = new EventEmitter()
  @Output() onFocus: EventEmitter<any> = new EventEmitter()
  @Output() onBlur: EventEmitter<any> = new EventEmitter()
  @Output() onClose: EventEmitter<any> = new EventEmitter()
  @Output() onShow: EventEmitter<any> = new EventEmitter()

  constructor(
    private config: PrimeNGConfig,
    private zone: NgZone,
    public el: ElementRef,
    public renderer: Renderer2,
    public cd: ChangeDetectorRef
  ) {}

  setDisplayValue() {
    this.displayValue = `${this.pad(this.displayMinutes, 2)}:${this.pad(
      this.displaySeconds,
      2
    )}`
    this.totalSeconds = this.displayMinutes * 60 + this.displaySeconds
    this.ngModelChange.emit(this.totalSeconds)
    this.onModelChange(this.totalSeconds)
  }

  parseDisplayValue(event) {
    this.displayMinutes = Number(event.split(':')[0])
    let newSeconds = Number(event.split(':')[1])
    this.setDisplaySeconds(newSeconds)
    this.totalSeconds = this.displayMinutes * 60 + this.displaySeconds
  }

  setDisplaySeconds(newSeconds: number) {
    if (newSeconds > 59) {
      newSeconds = newSeconds - 60
      if (this.displayMinutes < 99) this.displayMinutes++
    }
    this.displaySeconds = newSeconds
    this.setDisplayValue()
  }

  pad(num: number, size: number): string {
    let val = num.toString()
    while (val.length < size) val = '0' + val
    return val
  }

  incrementMinute(event) {
    let newMinutes = this.displayMinutes + 1
    newMinutes = newMinutes > 59 ? newMinutes - 60 : newMinutes
    this.displayMinutes = newMinutes
    this.setDisplayValue()
    event.preventDefault()
  }

  decrementMinute(event) {
    let newMinutes = this.displayMinutes - 1
    newMinutes = newMinutes < 0 ? 60 + newMinutes : newMinutes
    this.displayMinutes = newMinutes
    this.setDisplayValue()
    event.preventDefault()
  }

  incrementSecond(event) {
    let newSeconds = this.displaySeconds + 1
    this.setDisplaySeconds(newSeconds)
    event.preventDefault()
  }

  decrementSecond(event) {
    let newSeconds = this.displaySeconds - 1
    if (newSeconds < 0) {
      newSeconds = 60 + newSeconds
      if (this.displayMinutes > 0) this.displayMinutes--
    }
    this.displaySeconds = newSeconds
    this.setDisplayValue()
    event.preventDefault()
  }

  onTimePickerElementMouseDown(event: Event, type: number, direction: number) {
    if (!this.disabled) {
      this.repeat(event, null, type, direction)
      event.preventDefault()
    }
  }

  onTimePickerElementMouseUp(event: Event) {
    if (!this.disabled) {
      this.clearTimePickerTimer()
    }
  }

  onTimePickerElementMouseLeave() {
    if (!this.disabled && this.timePickerTimer) {
      this.clearTimePickerTimer()
    }
  }

  repeat(event: Event, interval: number, type: number, direction: number) {
    let i = interval || 500

    this.clearTimePickerTimer()
    this.timePickerTimer = setTimeout(() => {
      this.repeat(event, 100, type, direction)
      this.cd.markForCheck()
    }, i)

    switch (type) {
      // case 0:
      // Easy to add hours if needed, could even add an input to decide to total with/without hours
      //     if (direction === 1)
      //         this.incrementHour(event);
      //     else
      //         this.decrementHour(event);
      // break;

      case 1:
        if (direction === 1) this.incrementMinute(event)
        else this.decrementMinute(event)
        break

      case 2:
        if (direction === 1) this.incrementSecond(event)
        else this.decrementSecond(event)
        break
    }
  }

  clearTimePickerTimer() {
    if (this.timePickerTimer) {
      clearTimeout(this.timePickerTimer)
      this.timePickerTimer = null
    }
  }

  onContainerButtonKeydown(event) {
    switch (event.which) {
      //tab
      case 9:
        if (!this.inline) {
          this.trapFocus(event)
        }
        break

      //escape
      case 27:
        this.overlayVisible = false
        event.preventDefault()
        break

      default:
        //Noop
        break
    }
  }

  //#region Overlay

  onOverlayAnimationStart(event: AnimationEvent) {
    switch (event.toState) {
      case 'visible':
        if (!this.inline) {
          this.overlay = event.element
          this.appendOverlay()
          if (this.autoZIndex) {
            ZIndexUtils.set(
              'overlay',
              this.overlay,
              this.baseZIndex || this.config.zIndex.overlay
            )
          }
          this.onShow.emit(event)
        }
        break
      case 'void':
        this.onOverlayHide()
        this.onClose.emit(event)
        break
    }
  }

  onOverlayAnimationDone(event: AnimationEvent) {
    switch (event.toState) {
      case 'visible':
        if (!this.inline) {
          this.bindDocumentClickListener()
          this.bindDocumentResizeListener()
          this.bindScrollListener()
        }
        break
      case 'void':
        if (this.autoZIndex) {
          ZIndexUtils.clear(event.element)
        }
        break
    }
  }

  appendOverlay() {
    if (this.appendTo) {
      if (this.appendTo === 'body') document.body.appendChild(this.overlay)
      else DomHandler.appendChild(this.overlay, this.appendTo)
    }
  }

  onOverlayHide() {
    this.unbindDocumentClickListener()
    this.unbindDocumentResizeListener()
    this.unbindScrollListener()
    this.overlay = null
  }

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentTarget: any = this.el
          ? this.el.nativeElement.ownerDocument
          : 'document'

        this.documentClickListener = this.renderer.listen(
          documentTarget,
          'mousedown',
          (event) => {
            if (this.isOutsideClicked(event) && this.overlayVisible) {
              this.zone.run(() => {
                this.hideOverlay()

                this.cd.markForCheck()
              })
            }
          }
        )
      })
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener()
      this.documentClickListener = null
    }
  }

  bindDocumentResizeListener() {
    if (!this.documentResizeListener) {
      this.documentResizeListener = this.onWindowResize.bind(this)
      window.addEventListener('resize', this.documentResizeListener)
    }
  }

  unbindDocumentResizeListener() {
    if (this.documentResizeListener) {
      window.removeEventListener('resize', this.documentResizeListener)
      this.documentResizeListener = null
    }
  }

  bindScrollListener() {
    if (!this.scrollHandler) {
      this.scrollHandler = new ConnectedOverlayScrollHandler(
        this.containerViewChild.nativeElement,
        () => {
          if (this.overlayVisible) {
            this.hideOverlay()
          }
        }
      )
    }

    this.scrollHandler.bindScrollListener()
  }

  unbindScrollListener() {
    if (this.scrollHandler) {
      this.scrollHandler.unbindScrollListener()
    }
  }

  isOutsideClicked(event: Event) {
    return !(
      this.el.nativeElement.isSameNode(event.target) ||
      this.el.nativeElement.contains(event.target) ||
      (this.overlay && this.overlay.contains(<Node>event.target))
    )
  }

  hideOverlay() {
    this.overlayVisible = false
    this.cd.markForCheck()
  }

  onWindowResize() {
    if (this.overlayVisible && !DomHandler.isAndroid()) {
      this.hideOverlay()
    }
  }

  onInputFocus(event: Event) {
    this.focus = true
    if (this.showOnFocus) {
      this.showOverlay()
    }
    this.onFocus.emit(event)
  }

  onInputBlur(event: Event) {
    this.focus = false
    this.onModelTouched()
    this.onBlur.emit(event)
  }

  onInputClick() {
    if (this.showOnFocus && !this.overlayVisible) {
      this.showOverlay()
    }
  }

  showOverlay() {
    if (!this.overlayVisible) {
      this.overlayVisible = true
    }
  }

  trapFocus(event) {
    let focusableElements = DomHandler.getFocusableElements(
      this.contentViewChild.nativeElement
    )

    if (focusableElements && focusableElements.length > 0) {
      if (!focusableElements[0].ownerDocument.activeElement) {
        focusableElements[0].focus()
      } else {
        let focusedIndex = focusableElements.indexOf(
          focusableElements[0].ownerDocument.activeElement
        )

        if (event.shiftKey) {
          if (focusedIndex == -1 || focusedIndex === 0) {
            if (this.focusTrap) {
              focusableElements[focusableElements.length - 1].focus()
            } else {
              if (focusedIndex === -1) return this.hideOverlay()
              else if (focusedIndex === 0) return
            }
          } else {
            focusableElements[focusedIndex - 1].focus()
          }
        } else {
          if (
            focusedIndex == -1 ||
            focusedIndex === focusableElements.length - 1
          ) {
            if (!this.focusTrap && focusedIndex != -1) return this.hideOverlay()
            else focusableElements[0].focus()
          } else {
            focusableElements[focusedIndex + 1].focus()
          }
        }
      }
    }

    event.preventDefault()
  }

  //#endregion

  registerOnChange(fn: Function): void {
    this.onModelChange = fn
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn
  }

  setDisabledState(val: boolean): void {
    this.disabled = val
    this.cd.markForCheck()
  }
}
